/*
 * Virtualisierung fuer lange Zeilenlisten fester Hoehe.
 *
 * Herausgezogen aus `charts/MultiLeafPicker.tsx`, wo sie seit der DP-Charts-
 * Seite steht und sich bewaehrt hat. Die Rechnung ist unveraendert
 * uebernommen (Fensteranfang, Ueberhang, Gesamthoehe), damit das Herausziehen
 * am Chart-Waehler nichts aendert; neu ist nur, dass der HMI-Datenpunkt-
 * Waehler dieselbe Mechanik benutzt statt einer zweiten Fassung davon.
 *
 * Bewusst keine Bibliothek: react-window koennte das auch, aber vierzig
 * Zeilen ohne Abhaengigkeit sind leichter zu lesen als die Adapterschicht,
 * die preact/compat dafuer braeuchte.
 *
 * Voraussetzung: alle Zeilen sind gleich hoch und werden absolut positioniert
 * in einem Abstandhalter der Hoehe `totalHeight` gesetzt.
 */

import { useCallback, useEffect, useRef, useState } from 'preact/hooks';

export interface VirtualRows {
  /** An den scrollenden Container haengen. */
  scrollRef: { current: HTMLDivElement | null };
  /** Erste zu rendernde Zeile (inklusive), Ueberhang schon abgezogen. */
  startIndex: number;
  /** Letzte zu rendernde Zeile (exklusive), Ueberhang schon addiert. */
  endIndex: number;
  /** Hoehe des Abstandhalters, damit die Bildlaufleiste stimmt. */
  totalHeight: number;
  /** Nach oben springen. Gebraucht, wenn sich die Liste unter dem Finger
   *  austauscht (Suche statt Baum), sonst steht man im Nichts. */
  scrollToTop(): void;
}

export function useVirtualRows(count: number, rowHeight: number, overscan: number): VirtualRows {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  // 400 als Startwert: bis der ResizeObserver das erste Mal misst, wird ein
  // plausibles Fenster gerendert statt gar keins.
  const [viewportHeight, setViewportHeight] = useState(400);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => setScrollTop(el.scrollTop);
    const observer = new ResizeObserver(() => setViewportHeight(el.clientHeight));
    el.addEventListener('scroll', onScroll, { passive: true });
    observer.observe(el);
    setViewportHeight(el.clientHeight);
    return () => {
      el.removeEventListener('scroll', onScroll);
      observer.disconnect();
    };
  }, []);

  const scrollToTop = useCallback(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = 0;
    setScrollTop(0);
  }, []);

  const totalHeight = count * rowHeight;
  const startIndex = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan);
  const endIndex = Math.min(count, Math.ceil((scrollTop + viewportHeight) / rowHeight) + overscan);

  return { scrollRef, startIndex, endIndex, totalHeight, scrollToTop };
}
